import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';

import './index.css';
import { Languages } from './Config/I18n';
import { Error404 } from './Pages/Error404Page';
import { Home } from './Pages/HomePage';
import { Llibret } from './Pages/Historico/LlibretPage';
import { Login } from './Pages/LogInPage';
import { Premios } from './Pages/Galeria/PremiosPage';
import { Loader } from './Pages/LoadingPage';
import { CalendarioRodrigo } from './Pages/Ejercicio/CalendarPage';
import { News } from './Pages/Ejercicio/NewsPage';
import { RepresentantesActuales } from './Pages/Ejercicio/RepresentantesActualesPage';
import { RepresentantesAnteriores } from './Pages/Historico/RepresentantesAnterioresPage';
import { Contactarnos } from './Pages/Nosotros/ContactFormPage';
import { Terminos } from './Pages/TerminosPage';
import { Politica } from './Pages/PoliticaPage';
import { ArteFallero } from './Pages/Galeria/ArtePage';
import { CrearEncuesta } from './Pages/Encuestas/CrearEncuestaPage';
import { Perfil } from './Pages/Usuarios/PerfilPage';
import { Encuesta } from './Pages/Encuestas/EncuestaPage';
import { IonApp, setupIonicReact } from '@ionic/react';
import { CrearNotificacion } from './Pages/Notificaciones/CrearNotificacionPage';
import { Notificaciones } from './Pages/Notificaciones/NotificacionesPage';
import { AdministracionUsuarios } from './Pages/Usuarios/AdministracionUsuariosPage';
import { CrearUsuario } from './Pages/Usuarios/CrearUsuarioPage';
import axios from 'axios';
import { EditarUsuario } from './Pages/Usuarios/EditarUsuarioPage';
import { GestionEncuestas } from './Pages/Encuestas/GestionEncuestasPage';
import { ContestarEncuesta } from './Pages/Encuestas/ContestarEncuestaPage';
import { PagoEncuestaKoPage } from './Pages/Encuestas/PagoEncuestaKoPage';
import { PagoEncuestaOkPage } from './Pages/Encuestas/PagoEncuestaOkPage';
import { PagoEncuestaRedirectPage } from './Pages/Encuestas/PagoEncuestaRedirectPage';
import { Upload } from './Pages/UploadPage';
import { ContactMessagesPage } from './Pages/Nosotros/ContactMessagesPage';
import { AdminSponsorPage } from './Pages/Patrocinadores/AdminSponsorPage';
import { SponsorsListPage } from './Pages/Patrocinadores/SponsorsListPage';
import { ValidateQRPage } from './Pages/Patrocinadores/ValidateQRPage';
import { QRValidationPage } from './Pages/Patrocinadores/QRValidationPage';
import { SponsorFormPage } from './Pages/Patrocinadores/SponsorFormPage';
import { Publicidad } from './Pages/Patrocinadores/Publicidad';
import { AddUserPage } from './Pages/AddUserPage';
import setupNotifications, { clearNativeOneSignalNotifications, linkOneSignalUser } from './NotificationService';
import { HistoriaPage } from './Pages/Historico/HistoriaPage';
import { Canciones } from './Pages/CancionesPage';
import { SponsorsPage } from './Pages/SponsorsPage';
import { EditarProfilePage } from './Pages/Usuarios/EditProfilePage';
import { Descargas } from './Pages/DescargasPage';
import { PersonalStatsPage } from './Pages/Wrapped/PersonalStatsPage';
import { PersonalTrackerPage } from './Pages/Wrapped/PersonalTrakerPage';
import { AdminLayout } from './Components/AdminLayout';
import { View } from './Pages/VirwPage';
import ExportarEncuesta from './Pages/Encuestas/ExportarEncuesta';
import { InAppNotificationProvider, useInAppNotifications } from './Contexts/InAppNotificationContext';
import { clearUnreadBadge, incrementUnreadBadge } from './BadgeService';
import { initSessionTracking, markNotificationOpen } from './AnalyticsService';
import { EstadisticasApp } from './Pages/EstadisticasAppPage';
import { getActiveProfileToken, setActiveProfileToken } from './utils/profileStorage';
import { GetBocetos, GetLlibrets, GetPremios, GetRepresentantes } from './Backend/ApiCalls';

setupIonicReact();

const APP_VERSION_URL = 'https://api.fallaplazarodrigo.es:999/App/Version';

const normalizeVersionPart = (part) => {
  const parsed = Number.parseInt(String(part || '0').replace(/\D/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const compareVersions = (current, target) => {
  const currentParts = String(current || '0').split('.').map(normalizeVersionPart);
  const targetParts = String(target || '0').split('.').map(normalizeVersionPart);
  const length = Math.max(currentParts.length, targetParts.length, 3);

  for (let index = 0; index < length; index += 1) {
    const currentValue = currentParts[index] || 0;
    const targetValue = targetParts[index] || 0;
    if (currentValue > targetValue) return 1;
    if (currentValue < targetValue) return -1;
  }

  return 0;
};

const waitForAllSettled = (promises) => Promise.all(
  promises.map((promise) => Promise.resolve(promise).then(
    (value) => ({ status: 'fulfilled', value }),
    (reason) => ({ status: 'rejected', reason })
  ))
);

const NotificationBootstrap = ({ router, currentUser, children }) => {
  const notificationApi = useInAppNotifications();
  const notificationApiRef = React.useRef(notificationApi);

  notificationApiRef.current = notificationApi;

  const clearAppBadges = React.useCallback(() => {
    clearUnreadBadge();
    clearNativeOneSignalNotifications();
  }, []);

  useEffect(() => {
    let isMounted = true;
    let cleanupListeners = null;

    const configureNotifications = async () => {
      cleanupListeners = await setupNotifications({
          onForegroundNotification: (notification) => {
          if (!isMounted) {
            return;
          }

          incrementUnreadBadge(1);
          notificationApiRef.current.showNotification({
            ...notification,
            route: notification.route || '/notificaciones'
          });
        },
        onNotificationClick: (notification) => {
          if (!isMounted) {
            return;
          }

          // Analítica: la app se ha abierto desde una notificación (guardamos cuál)
          markNotificationOpen(notification);

          router.navigate(notification.route || '/notificaciones');
        }
      });

      if (isMounted) {
        clearAppBadges();
      }
    };

    configureNotifications();

    // Analítica propia: registra el inicio de sesión de uso (web/app/notificación)
    initSessionTracking();

    return () => {
      isMounted = false;

      if (typeof cleanupListeners === 'function') {
        cleanupListeners();
 }
    };
  }, [router, clearAppBadges]);

  useEffect(() => {
    clearAppBadges();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearAppBadges();
      }
    };

    const handleWindowFocus = () => {
      clearAppBadges();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [clearAppBadges]);

  return children;
};

const NativePaymentBootstrap = ({ router, children }) => {
  useEffect(() => {
    let appListener = null;

    const setupDeepLinks = async () => {
      if (!window?.Capacitor?.isNativePlatform || !window.Capacitor.isNativePlatform()) {
        return;
      }

      const [{ App }, browserModule] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/browser').catch(() => null),
      ]);

      appListener = await App.addListener('appUrlOpen', ({ url }) => {
        if (!url || !url.startsWith('fallaplazarodrigo://payment/')) {
          return;
        }

        try {
          const parsed = new URL(url);
          const isOk = parsed.pathname === '/ok';
          const target = `${isOk ? '/pago-ok' : '/pago-ko'}${parsed.search || ''}`;

          if (browserModule?.Browser?.close) {
            browserModule.Browser.close().catch(() => {});
          }

          router.navigate(target);
        } catch (error) {
          console.error('Error procesando deep link de pago:', error);
        }
      });
    };

    setupDeepLinks();

    return () => {
      if (appListener?.remove) {
        appListener.remove();
      }
    };
  }, [router]);

  return children;
};

const AppUpdateBootstrap = ({ children }) => {
  const [updateInfo, setUpdateInfo] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const checkForUpdates = async () => {
      try {
        if (!window?.Capacitor?.isNativePlatform || !window.Capacitor.isNativePlatform()) {
          return;
        }

        const [{ App }] = await Promise.all([
          import('@capacitor/app'),
        ]);
        const appInfo = await App.getInfo();
        const platform = String(window.Capacitor.getPlatform?.() || '').toLowerCase();

        if (platform !== 'ios') {
          return;
        }

        const response = await fetch(APP_VERSION_URL, { cache: 'no-store' });
        if (!response.ok) return;

        const versionConfig = await response.json();
        const config = versionConfig?.ios;
        if (!config?.latestVersion || !config?.storeUrl) return;

        const installedVersion = appInfo?.version || '0.0.0';
        const minimumVersion = config.minimumVersion || config.latestVersion;
        const isBelowMinimum = compareVersions(installedVersion, minimumVersion) < 0;
        const isBehindLatest = compareVersions(installedVersion, config.latestVersion) < 0;
        const forceUpdate = isBelowMinimum || (Boolean(config.forceUpdate) && isBehindLatest);

        if (!isBehindLatest && !isBelowMinimum) return;

        const dismissedKey = `app-update-dismissed-ios-${config.latestVersion}`;
        if (!forceUpdate && localStorage.getItem(dismissedKey) === '1') return;

        if (!isMounted) return;

        const nextUpdateInfo = {
          ...config,
          installedVersion,
          forceUpdate,
          dismissedKey,
        };
        const message = [
          forceUpdate
            ? (config.forceMessage || 'Necesitas actualizar la app para continuar.')
            : (config.message || 'Hay una nueva version disponible.'),
          '',
          `Version instalada: ${installedVersion}`,
          `Nueva version: ${config.latestVersion}`,
          '',
          'Pulsa OK para ir a la App Store.',
        ].join('\n');

        const wantsUpdate = window.confirm(message);

        if (wantsUpdate) {
          if (dismissedKey) {
            localStorage.setItem(dismissedKey, '1');
          }
          await openStore(config.storeUrl);
          return;
        }

        if (forceUpdate) {
          setUpdateInfo(nextUpdateInfo);
          return;
        }

      } catch (error) {
        console.error('Error comprobando actualizaciones:', error);
      }
    };

    checkForUpdates();

    return () => {
      isMounted = false;
    };
  }, []);

  const openStore = async (storeUrl = updateInfo?.storeUrl) => {
    if (!storeUrl) return;

    try {
      const browserModule = await import('@capacitor/browser').catch(() => null);
      if (browserModule?.Browser?.open) {
        await browserModule.Browser.open({ url: storeUrl });
        return;
      }
    } catch (error) {
      console.error('Error abriendo App Store:', error);
    }

    window.location.href = storeUrl;
  };

  const dismissUpdate = () => {
    if (updateInfo?.dismissedKey) {
      localStorage.setItem(updateInfo.dismissedKey, '1');
    }
    setUpdateInfo(null);
  };

  return (
    <>
      {children}
      {updateInfo && (
        <div className="app-update-overlay" role="dialog" aria-modal="true">
          <div className="app-update-modal">
            <h2>{updateInfo.title || 'Actualizacion disponible'}</h2>
            <p>
              {updateInfo.forceUpdate
                ? (updateInfo.forceMessage || 'Necesitas actualizar la app para continuar.')
                : (updateInfo.message || 'Hay una nueva version disponible.')}
            </p>
            <p className="app-update-version">
              Version instalada: {updateInfo.installedVersion} / Nueva version: {updateInfo.latestVersion}
            </p>
            <div className="app-update-actions">
              {!updateInfo.forceUpdate && (
                <button type="button" className="app-update-secondary" onClick={dismissUpdate}>
                  Ahora no
                </button>
              )}
              <button type="button" className="app-update-primary" onClick={openStore}>
                Actualizar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Index = () => {
  document.title = "Falla Plaza Rodrigo🧡";
  let props = {
    I18n: Languages.Spanish,
    Language: localStorage.getItem("Language"),
    PagesData: {
      Premios: [],
      Llibrets: [],
      Representantes: [],
      UserData: []
    }
  };

  // I18n
  switch (props.Language) {
    case null:
      localStorage.setItem("Language", "Spanish");
      props.Language = localStorage.getItem("Language");
      break;
    case "Valencian":
      props.I18n = Languages.Valencian;
      break;
    default:
      break;
  }

  const [loading, setLoading] = useState(true);
  const [premiosData, setPremiosData] = useState([]);
  const [llibretsData, setLlibretsData] = useState([]);
  const [representantesData, setRepresentantesData] = useState([]);
  const [arteData, setArteData] = useState([]);
  const [user, setUser] = useState(null);

  // Sincroniza el token localStorage→sessionStorage. La sincronización
  // *crítica* ya la hace el <script> inline de index.html antes incluso de
  // cargar React, así no hace falta recargar la página. Esto es solo por si
  // cambia en runtime.
  const activeToken = getActiveProfileToken();
  if (activeToken && !sessionStorage.getItem('token')) {
    setActiveProfileToken(activeToken);
  }

  // ─────────────────────────────────────────────────────────────────────
  // BOOTSTRAP: carga en paralelo TODO lo que necesita la app antes de
  // ocultar el Loader. Si quitamos loading=false antes de que `user` esté
  // listo, se ven flashes con "Desconocido" en las páginas.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const API_BASE = "https://api.fallaplazarodrigo.es:999";

    const loadUser = async () => {
      const token = getActiveProfileToken();
      if (!token) return; // sin token no hay user que cargar
      try {
        const response = await axios.get(`${API_BASE}/Users/GetAccountByToken`, {
          headers: { 'Content-Type': 'application/json', token }
        });
        if (response.status === 200 && !cancelled) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Error cargando user:', error);
      }
    };

    const loadPremios = async () => {
      if (premiosData.length > 0) return;
      try {
        const { response, data } = await GetPremios();
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (!cancelled) setPremiosData(data);
      } catch (e) { console.error('premios load:', e); }
    };

    const loadLlibrets = async () => {
      if (llibretsData.length > 0) return;
      try {
        const { response, data } = await GetLlibrets();
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (!cancelled) setLlibretsData(data);
      } catch (e) { console.error('llibrets load:', e); }
    };

    const loadRepresentantes = async () => {
      if (representantesData.length > 0) return;
      try {
        const { response, data } = await GetRepresentantes();
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (!cancelled) setRepresentantesData(data);
      } catch (e) { console.error('representantes load:', e); }
    };

    const loadArte = async () => {
      if (arteData.length > 0) return;
      try {
        const { response, data } = await GetBocetos();
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (!cancelled) setArteData(data);
      } catch (e) { console.error('arte load:', e); }
    };

    (async () => {
      // Esperamos a TODAS las cargas (éxito o fallo) antes de quitar el Loader.
      await waitForAllSettled([
        loadUser(),
        loadPremios(),
        loadLlibrets(),
        loadRepresentantes(),
        loadArte(),
      ]);
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vincula la suscripción push de este dispositivo con el usuario logueado
  // (external_id de OneSignal). Necesario para poder enviar push individuales.
  useEffect(() => {
    if (user?.Id) {
      linkOneSignalUser(user.Id);
    }
  }, [user]);


  props.PagesData.Premios = premiosData;
  props.PagesData.Llibrets = llibretsData;
  props.PagesData.Representantes = representantesData;
  props.PagesData.Arte = arteData;

  if (user) {
    props.PagesData.UserData = user;
  }

  const ProtectedRoute = ({ element }) => {
    return getActiveProfileToken() ? (
      element
    ) : (
      <Navigate to="/login" replace />
    );
  };

  // Ruta protegida por rango. Por defecto exige Administrador (3). Con
  // minRank={2} también deja pasar al Vicepresidente — útil para el panel
  // de gestión de encuestas (crear, editar, eliminar, ver respuestas).
  const AdminRoute = ({ element, minRank = 3 }) => {
    const userToken = getActiveProfileToken();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      async function fetchUser() {
        try {
          const response = await axios.get('https://api.fallaplazarodrigo.es:999/Users/GetAccountByToken', {
            headers: {
              'Content-Type': 'application/json',
              'token': userToken
            }
          });
          if (response.status === 200) {
            setUser(response.data);
          } else {
            if (response.status === 401) {
              sessionStorage.removeItem('token');
              window.location.href = "/login";
            }
            console.error(response.data.Message);
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            sessionStorage.removeItem('token');
            window.location.href = "/login";
          } else {
            console.error('Error en la solicitud:', error);
          }
        } finally {
          setIsLoading(false);
        }
      }
      fetchUser();
    }, [userToken]);

    useEffect(() => {
      // Solo funciona dentro de la app instalada (no en navegador)
      if (window.cordova) {
        try {
          console.log("🟢 LIMPIANDO BADGE Y NOTIFICACIONES…");

          clearNativeOneSignalNotifications();
          clearUnreadBadge();

          console.log("🟢 Badge limpiado.");
        } catch (err) {
          console.error("❌ Error limpiando badge:", err);
        }
      }
    }, []);


    if (isLoading) {
      return <Loader />;
    }

    return user && Number(user.userRank) >= minRank ? (
      element
    ) : (
      <Navigate to="/login" replace />
    );
  };

  const pageRouter = useMemo(() => createBrowserRouter([
    {
      path: "*",
      element: <Error404 I18n={props.I18n} />
    },
    {
      path: "/",
      element: <Home I18n={props.I18n} />
    },
    {
      path: "/llibret",
      element: <Llibret I18n={props.I18n} Data={props.PagesData.Llibrets} />
    },
    {
      path: "/login",
      element: getActiveProfileToken() ? <Navigate to="/perfil" replace /> : <Login I18n={props.I18n} />
    },
    {
      path: "/premios-y-reconocimientos",
      element: <Premios I18n={props.I18n} Data={props.PagesData.Premios} />
    },
    {
      path: "/calendario-rodrigo",
      element: <CalendarioRodrigo I18n={props.I18n} User={props.PagesData.UserData} />
    },
    {
      path: "/Noticias",
      element: <News I18n={props.I18n} />
    },
    {
      path: "/representantes-actuales",
      element: <RepresentantesActuales Data={props.PagesData.Representantes} I18n={props.I18n} />
    },
    {
      path: "/representantes-anteriores",
      element: <RepresentantesAnteriores Data={props.PagesData.Representantes} I18n={props.I18n} />
    },
    {
      path: "/formulario-de-contacto",
      element: <Contactarnos I18n={props.I18n} />
    },
    {
      path: "/terminos-y-condiciones",
      element: <Terminos I18n={props.I18n} />
    },
    {
      path: "/politica-de-privacidad",
      element: <Politica I18n={props.I18n} />
    },
    {
      path: "/arte-fallero",
      element: <ArteFallero I18n={props.I18n} Data={props.PagesData.Arte} />
    },
    {
      path: "/editar-encuesta/:id", element: <AdminRoute minRank={2} element={<CrearEncuesta I18n={props.I18n} />} />
    },
    {
      path: "/contestar-encuesta",
      element: <ProtectedRoute element={<Encuesta I18n={props.I18n} User={props.PagesData.UserData} />} />
    },
    {
      path: "/perfil",
      element: <ProtectedRoute element={<Perfil I18n={props.I18n} User={props.PagesData.UserData} />} />
    },
    {
      path: "/editar-notificacion/:id",
      element: <AdminRoute element={<CrearNotificacion I18n={props.I18n} />} />
    },
    {
      path: "/notificaciones",
      element: <ProtectedRoute element={<Notificaciones I18n={props.I18n} User={props.PagesData.UserData} />} />
    },
    {
      path: "/admin",
      element: <AdminRoute element={<AdminLayout />} />,
      children: [
        {
          path: "administracion-usuarios",
          element: <AdministracionUsuarios I18n={props.I18n} User={props.PagesData.UserData} />
        },
        {
          path: "crear-notificacion",
          element: <CrearNotificacion I18n={props.I18n} User={props.PagesData.UserData} />
        },
        {
          path: "crear-encuesta",
          element: <CrearEncuesta I18n={props.I18n} User={props.PagesData.UserData} />
        },
        {
          path: "subida-de-imagenes",
          element: <Upload I18n={props.I18n} User={props.PagesData.UserData} />
        },
        {
          path: "mensajes",
          element: <ContactMessagesPage I18n={props.I18n} User={props.PagesData.UserData} />
        },
        {
          path: "estadisticas",
          element: <EstadisticasApp I18n={props.I18n} User={props.PagesData.UserData} />
        }
      ]
    },
    {
      path: "/crear-usuario",
      element: <AdminRoute element={<CrearUsuario I18n={props.I18n} />} />
    },
    {
      path: "/editar-usuario/:userId",
      element: <AdminRoute element={<EditarUsuario I18n={props.I18n} />} />
    },
    {
      path: "/gestionar-encuestas",
      element: <AdminRoute minRank={2} element={<GestionEncuestas I18n={props.I18n} User={props.PagesData.UserData} />} />
    },
    {
      path: "/contestar-encuesta/:pollid",
      element: <ProtectedRoute element={<ContestarEncuesta I18n={props.I18n} User={props.PagesData.UserData} />} />
    },
    {
      // A propósito SIN ProtectedRoute (Parte 12/13/16): esta pantalla se
      // autentica con el receiptToken de la propia URL, no con la sesión.
      // Con ProtectedRoute, quien volvía de Redsys en un navegador externo
      // (sin sessionStorage de la app) acababa redirigido a /login en vez
      // de ver su recibo — esa era la causa exacta del problema reportado.
      path: "/pago-ok",
      element: <PagoEncuestaOkPage />
    },
    {
      path: "/pago-ko",
      element: <PagoEncuestaKoPage />
    },
    {
      path: "/pago-redsys",
      element: <ProtectedRoute element={<PagoEncuestaRedirectPage I18n={props.I18n} />} />
    },
    {
      path: "/admin/patrocinadores",
      element: <AdminRoute element={<AdminSponsorPage I18n={props.I18n} />} />
    },
    {
      path: "/admin/patrocinadores/nuevo",
      element: <AdminRoute element={<SponsorFormPage mode="create" I18n={props.I18n} />} />
    },
    {
      path: "/admin/patrocinadores/editar/:id",
      element: <AdminRoute element={<SponsorFormPage mode="edit" I18n={props.I18n} />} />
    },
    {
      path: "/descuentos",
      element: <ProtectedRoute element={<SponsorsListPage User={props.PagesData.UserData} I18n={props.I18n} />} />
    },
    {
      path: "/validate-qr",
      element: <ValidateQRPage I18n={props.I18n} />
    }
    , {
      path: "/valid-qr",
      element: <QRValidationPage I18n={props.I18n} />
    },
    {
      path: "/add-user",
      element: <ProtectedRoute element={<AddUserPage I18n={props.I18n} />} />
    },
    {
      path: "/nuestra-historia",
      element: <HistoriaPage I18n={props.I18n} />
    },
    {
      path: "/canciones-rodrigueras",
      element: <Canciones I18n={props.I18n} />
    },
    {
      path: "/patrocinadores",
      element: <SponsorsPage I18n={props.I18n} />
    },
    {
      path: "/editar-perfil",
      element: <ProtectedRoute element={<EditarProfilePage I18n={props.I18n} User={props.PagesData.UserData} />} />
    },
    {
      path: "/descargas",
      element: <Descargas I18n={props.I18n} User={props.PagesData.UserData} />
    },
    {
      path: "/actas/:id",
      element: <View I18n={props.I18n} />
    },
    {
      path: "/exportar-encuesta",
      element: <ExportarEncuesta />
    },
    , {
      path: "/publicidad",
      element: <ProtectedRoute element={<Publicidad I18n={props.I18n} User={props.PagesData.UserData} />} />
    },

    {
      path: "/personal",
      element: <ProtectedRoute element={<PersonalTrackerPage I18n={props.I18n} />} />
    },
    {
      path: "/personal-stats",
      element: <ProtectedRoute element={<PersonalStatsPage I18n={props.I18n} />} />
    }

  ]), [
    props.I18n,
    props.PagesData.Arte,
    props.PagesData.Llibrets,
    props.PagesData.Premios,
    props.PagesData.Representantes,
    props.PagesData.UserData,
  ]);

  return (
    <InAppNotificationProvider navigate={(to) => pageRouter.navigate(to)}>
      <NotificationBootstrap router={pageRouter} currentUser={user}>
        <NativePaymentBootstrap router={pageRouter}>
          <AppUpdateBootstrap>
            {loading ? <Loader /> : <RouterProvider router={pageRouter} />}
          </AppUpdateBootstrap>
        </NativePaymentBootstrap>
      </NotificationBootstrap>
    </InAppNotificationProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <IonApp>
    <Index />
  </IonApp>
);
